/**
 * AdjustmentReviewDialog — vendimi mbi një rregullim page.
 *
 * ── Pse ekziston si panel i veçantë ─────────────────────────────────────────
 * Aprovimi ishte një `<select>` brenda formularit të regjistrimit: i njëjti
 * dialog mbante shumën, periudhën, arsyen DHE statusin. Kjo i përziente dy punë
 * që nuk kanë të bëjnë me njëra-tjetrën — shkrimi i një rregullimi dhe vendimi
 * mbi të — dhe e bënte vendimin një klikim mbi një listë, pa asnjë vend ku të
 * lexohej ç'po vendosej. Refuzimi nuk kishte fare arsye: punonjësi lexonte
 * "Refuzuar" dhe kaq.
 *
 * Këtu vendimi ka faqen e vet: kërkesa e plotë, arsyetimi, dokumenti dhe
 * kronologjia — pastaj butonat. Aprovimi me një klikim nga rreshti u lë me
 * qëllim (i njëjti rregull si Qendra e Aprovimeve, shih docs/notes/ui_conventions.md):
 * "Shqyrto" çon te detaji, dhe vendimi konfirmohet ku arsyetimi rri para syve.
 *
 * Butonat vijnë nga `allowedActions()` te config/payrollAdjustments.js — të
 * njëjtat kushte që zbaton serveri, ndaj UI-ja nuk ofron veprime që kthehen 409.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Stack, TextField, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon      from '@mui/icons-material/CancelRounded';
import SendRoundedIcon        from '@mui/icons-material/SendRounded';
import UndoRoundedIcon        from '@mui/icons-material/UndoRounded';
import DownloadIcon           from '@mui/icons-material/Download';
import LockIcon               from '@mui/icons-material/Lock';
import PictureAsPdfIcon       from '@mui/icons-material/PictureAsPdf';
import PrintIcon             from '@mui/icons-material/Print';
import EmailIcon             from '@mui/icons-material/EmailOutlined';
import StatusChip from '../common/StatusChip';
import { authFetch } from '../../services/apiServices';
import { MONTHS_SQ } from '../../utils/period';
import {
  downloadDecisionPdf, printDecisionPdf, decisionPdfBase64, decisionFilename, decisionRef,
} from '../../services/adjustmentDecisionPdf';
import {
  CATEGORY_META, WORKFLOW, EVENT_LABELS, allowedActions, reasonOk, typeLabel, MIN_REASON,
} from '../../config/payrollAdjustments';

const fmt = (n) => Number(n || 0).toLocaleString('sq-AL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('sq-AL') : '—');
const fmtStamp = (d) => (d ? new Date(d).toLocaleString('sq-AL') : '—');
const periodOf = (r) => (r?.periodYear && r?.periodMonth ? `${MONTHS_SQ[r.periodMonth - 1]} ${r.periodYear}` : '—');

const ACTION_ICON = {
  approve: <CheckCircleRoundedIcon sx={{ fontSize: 17 }} />,
  reject:  <CancelRoundedIcon sx={{ fontSize: 17 }} />,
  submit:  <SendRoundedIcon sx={{ fontSize: 16 }} />,
  revoke:  <UndoRoundedIcon sx={{ fontSize: 17 }} />,
};

/** Etiketa e vlerës — çift etiketë/vlerë, i njëjti ritëm në krejt panelin. */
function Field({ label, value, mono = false, full = false }) {
  return (
    <Box sx={{ minWidth: full ? '100%' : 130, flex: full ? '1 1 100%' : '0 1 auto' }}>
      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: mono ? '0.95rem' : '0.83rem', fontWeight: mono ? 800 : 600, color: '#1F2937', lineHeight: 1.5 }}>
        {value}
      </Typography>
    </Box>
  );
}

export default function AdjustmentReviewDialog({
  open, row, actor = {}, company = {}, onClose, onDone, onEdit, onNotify,
}) {
  const theme = useTheme();
  const [events,  setEvents]  = useState(null);   // null = duke ngarkuar
  const [pending, setPending] = useState(null);   // veprimi që pret konfirmim
  const [reason,  setReason]  = useState('');
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState('');
  // Dërgimi i vendimit me email — hapi i vet, me marrës e shënim.
  const [mail,    setMail]    = useState(null);
  const [sending, setSending] = useState(false);

  const id = row?.id;
  const meta = row ? (CATEGORY_META[row.category] || {}) : {};
  const actions = useMemo(() => (row ? allowedActions(row, actor) : []), [row, actor]);

  // Kronologjia ngarkohet sa hapet paneli, jo bashkë me listën: një kërkesë për
  // rresht do të ishte dhjetëra kërkesa për një ekran që shpesh nuk hapet.
  const loadEvents = useCallback(async () => {
    if (!id) return;
    setEvents(null);
    try {
      const r = await authFetch(`/api/payroll-adjustments/${id}/events`);
      const j = await r.json().catch(() => ({}));
      setEvents(r.ok && j.success !== false ? (j.data || []) : []);
    } catch { setEvents([]); }
  }, [id]);

  useEffect(() => {
    if (!open) { setPending(null); setReason(''); setError(''); return; }
    loadEvents();
  }, [open, loadEvents]);

  const run = async () => {
    if (!pending || !id) return;
    const def = WORKFLOW[pending];
    if (def.reason && !reasonOk(reason)) return;
    setBusy(true); setError('');
    try {
      const r = await authFetch(`/api/payroll-adjustments/${id}/${pending}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason.trim() }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || j.errorMessage || 'Veprimi dështoi.');
      onDone?.(pending, j.data);
    } catch (e) {
      setError(e.message);
    } finally { setBusy(false); }
  };

  /**
   * Dërgimi i vendimit te punonjësi.
   *
   * PDF-i ndërtohet KËTU dhe shkon si bashkëngjitje: i njëjti dokument që sapo u
   * shkarkua ose u shtyp. Serveri nuk e ndërton të vetin — dy PDF-e për të
   * njëjtin vendim do të mund të ndryshonin pa e vënë re askush.
   */
  const sendMail = async () => {
    if (!mail?.to?.trim() || !id) return;
    setSending(true); setError('');
    try {
      const r = await authFetch(`/api/payroll-adjustments/${id}/decision-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: mail.to.trim(), cc: mail.cc.trim(), message: mail.message.trim(),
          pdfBase64: decisionPdfBase64(row, company, events || []),
          filename: decisionFilename(row),
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || j.errorMessage || 'Dërgimi dështoi.');
      setMail(null);
      onNotify?.(`Vendimi iu dërgua ${mail.to.trim()}.`);
      loadEvents();                       // dërgimi shfaqet te kronologjia
    } catch (e) {
      setError(e.message);
    } finally { setSending(false); }
  };

  if (!row) return null;

  const locked = row.status === 'processed';
  // Vendimi ekziston vetëm pasi është marrë: një draft nuk ka çka të nxjerrë në
  // letër dhe as çka t'i njoftojë punonjësit.
  const decided = ['approved', 'rejected', 'processed'].includes(row.status);
  const def = pending ? WORKFLOW[pending] : null;
  // Arsyeja e refuzimit është e detyrueshme; për veprimet e tjera fusha mbetet
  // shënim i lirë — një aprovim me kusht duhet të mund të shkruhet.
  const blocked = Boolean(def?.reason) && !reasonOk(reason);

  return (
    <Dialog open={open} onClose={() => !busy && onClose?.()} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <span>Shqyrto rregullimin</span>
          <StatusChip status={row.status} size="sm" />
          {row.origin === 'employee' && (
            <Tooltip title="Kërkesë e dërguar nga punonjësi">
              <Box component="span" sx={{
                fontSize: '0.62rem', fontWeight: 700, px: 0.75, py: 0.25, borderRadius: '6px',
                bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.dark',
              }}>
                VETËSHËRBIM
              </Box>
            </Tooltip>
          )}
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ px: 2.5 }}>
        {/* ── Kush, sa, për kur ── */}
        <Box sx={{
          p: 1.75, borderRadius: 2, mb: 2,
          bgcolor: meta.soft || '#F9FAFB', border: `1px solid ${meta.border || '#E5E7EB'}`,
        }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#111827' }}>
                {row.workerName || 'Punëtor i fshirë'}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#6B7280' }}>
                {meta.singular} · {typeLabel(row.category, row.type)}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
              <Typography sx={{ fontSize: '1.3rem', fontWeight: 800, color: meta.color, lineHeight: 1.2 }}>
                {meta.sign}€{fmt(row.amount)}
              </Typography>
              <Typography sx={{ fontSize: '0.65rem', color: '#6B7280' }}>
                {row.category === 'reimbursement' ? 'jashtë pagës' : 'në pagën e periudhës'}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2} sx={{ mb: 2 }}>
          <Field label="Periudha e pagës" value={periodOf(row)} />
          <Field label="Data efektive" value={fmtDate(row.effectiveDate)} />
          <Field label="Regjistroi" value={row.createdByName || '—'} />
          <Field label="Dërguar" value={row.submittedAt ? fmtStamp(row.submittedAt) : 'ende jo'} />
          {row.category === 'advance' && (
            <>
              <Field label="Kthimi" value={row.repaymentMethod === 'installments' ? `${row.installments || 1} këste` : 'njëherësh'} />
              <Field label="Mbetur" value={`€${fmt(row.remainingBalance ?? row.amount)}`} />
            </>
          )}
        </Stack>

        {/* Arsyetimi rri PARA vendimit dhe nuk fshihet kur mungon: një kërkesë pa
            arsye është e dhënë me peshë, jo hapësirë bosh. */}
        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#F9FAFB', border: '1px solid #F3F4F6', mb: 2 }}>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
            Arsyetimi i kërkesës
          </Typography>
          <Typography sx={{
            fontSize: '0.85rem', lineHeight: 1.6, whiteSpace: 'pre-wrap',
            color: row.description ? '#1F2937' : '#9CA3AF',
            fontStyle: row.description ? 'normal' : 'italic',
          }}>
            {row.description || 'Pa arsyetim të dhënë'}
          </Typography>
          {row.documentPath && (
            <Button size="small" startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
              onClick={() => window.open(`${process.env.REACT_APP_API_URL || ''}/api/payroll-adjustments/${row.id}/document`, '_blank')}
              sx={{ mt: 1, textTransform: 'none', fontWeight: 700, fontSize: '0.75rem' }}>
              {row.documentName || 'Dokumenti mbështetës'}
            </Button>
          )}
        </Box>

        {row.status === 'rejected' && (
          <Alert severity="error" sx={{ borderRadius: 2, mb: 2, fontSize: '0.82rem' }}>
            <strong>Refuzuar</strong>{row.approvedByName ? ` nga ${row.approvedByName}` : ''} · {fmtStamp(row.approvedAt)}
            <Box sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
              {row.rejectionReason || 'Pa arsye të regjistruar.'}
            </Box>
          </Alert>
        )}
        {row.status === 'approved' && (
          <Alert severity="success" sx={{ borderRadius: 2, mb: 2, fontSize: '0.82rem' }}>
            Aprovuar{row.approvedByName ? ` nga ${row.approvedByName}` : ''} · {fmtStamp(row.approvedAt)} — hyn në pagën e {periodOf(row)}.
          </Alert>
        )}
        {locked && (
          <Alert severity="info" icon={<LockIcon fontSize="small" />} sx={{ borderRadius: 2, mb: 2, fontSize: '0.82rem' }}>
            Procesuar te “{row.salaryListName || 'lista e pagës'}”. Vendimi nuk ndryshohet më —
            anuloje procesimin nga lista përkatëse.
          </Alert>
        )}

        {/* ── Dokumenti i vendimit ──────────────────────────────────────────
            Një dokument, tri rrugë: shkarkim, shtypje, email — të gjitha nga i
            njëjti `buildDoc`, që ajo çka mban punonjësi në dorë të jetë fjalë
            për fjalë ajo që u shtyp dhe u dërgua. */}
        {decided && !pending && (
          <Box sx={{
            mb: 2, p: 1.5, borderRadius: 2,
            border: '1px solid #E5E7EB', bgcolor: '#FCFCFD',
          }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#374151' }}>
                Vendimi Nr. {decisionRef(row)}
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', color: '#9CA3AF' }}>
                dokument zyrtar, gati për nënshkrim
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button size="small" variant="outlined" startIcon={<PictureAsPdfIcon sx={{ fontSize: 16 }} />}
                onClick={() => downloadDecisionPdf(row, company, events || [])}
                sx={{ textTransform: 'none', fontWeight: 700 }}>
                Shkarko PDF
              </Button>
              <Button size="small" variant="outlined" startIcon={<PrintIcon sx={{ fontSize: 16 }} />}
                onClick={() => {
                  if (!printDecisionPdf(row, company, events || [])) {
                    onNotify?.('Dritaret pop-up janë të bllokuara — PDF-ja u shkarkua në vend të shtypjes.', 'warning');
                  }
                }}
                sx={{ textTransform: 'none', fontWeight: 700 }}>
                Printo
              </Button>
              <Tooltip title={row.workerEmail ? `Dërgo te ${row.workerEmail}` : 'Ky punonjës nuk ka email të regjistruar'}>
                <span>
                  <Button size="small" variant="outlined" startIcon={<EmailIcon sx={{ fontSize: 16 }} />}
                    disabled={!actor.isDecider}
                    onClick={() => { setError(''); setMail({
                      to: row.workerEmail || '', cc: '', message: '',
                    }); }}
                    sx={{ textTransform: 'none', fontWeight: 700 }}>
                    Dërgo me email
                  </Button>
                </span>
              </Tooltip>
            </Stack>

            {mail && (
              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed #E5E7EB' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                  <TextField size="small" label="Marrësi" required fullWidth type="email"
                    value={mail.to} onChange={(e) => setMail((m) => ({ ...m, to: e.target.value }))}
                    helperText={row.workerEmail ? ' ' : 'Punonjësi nuk ka email të regjistruar — shkruaje me dorë.'} />
                  <TextField size="small" label="Kopje (cc)" fullWidth type="email"
                    value={mail.cc} onChange={(e) => setMail((m) => ({ ...m, cc: e.target.value }))}
                    helperText=" " />
                </Stack>
                <TextField size="small" label="Shënim shtesë (opsional)" fullWidth multiline rows={2}
                  sx={{ mt: 0.5 }}
                  value={mail.message} onChange={(e) => setMail((m) => ({ ...m, message: e.target.value }))}
                  helperText="Shtohet në trupin e emailit; PDF-i shkon i bashkëngjitur." />
              </Box>
            )}
          </Box>
        )}

        {/* ── Kronologjia ── */}
        <Divider textAlign="left" sx={{ mb: 1.25 }}>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Kronologjia
          </Typography>
        </Divider>
        {events === null ? (
          <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
        ) : events.length === 0 ? (
          <Typography sx={{ fontSize: '0.78rem', color: '#9CA3AF', fontStyle: 'italic' }}>
            Pa hapa të regjistruar.
          </Typography>
        ) : (
          <Stack spacing={0} sx={{ pl: 0.5 }}>
            {events.map((e, i) => (
              <Stack key={e.id} direction="row" spacing={1.25} alignItems="flex-start">
                {/* Vija vertikale e lidh hapin me të mëparshmin — radha lexohet
                    si rrugë, jo si listë e shkëputur. */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.75 }}>
                  <Box sx={{
                    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                    bgcolor: e.action === 'reject' ? theme.palette.error.main
                      : e.action === 'approve' ? theme.palette.success.main
                      : theme.palette.grey[400],
                  }} />
                  {i < events.length - 1 && <Box sx={{ width: 2, flex: 1, minHeight: 22, bgcolor: '#E5E7EB' }} />}
                </Box>
                <Box sx={{ pb: 1.25, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#1F2937' }}>
                    {EVENT_LABELS[e.action] || e.action}
                  </Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: '#9CA3AF' }}>
                    {e.actorEmail || 'sistemi'} · {fmtStamp(e.createdAt)}
                  </Typography>
                  {e.reason && (
                    <Typography sx={{ fontSize: '0.75rem', color: '#4B5563', mt: 0.25, whiteSpace: 'pre-wrap' }}>
                      {e.reason}
                    </Typography>
                  )}
                </Box>
              </Stack>
            ))}
          </Stack>
        )}

        {/* ── Konfirmimi ── */}
        {def && (
          <Box sx={{
            mt: 2, p: 1.75, borderRadius: 2,
            border: `1px solid ${alpha(theme.palette[def.tone].main, 0.4)}`,
            bgcolor: theme.palette[def.tone].soft || alpha(theme.palette[def.tone].main, 0.06),
          }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: theme.palette[def.tone].onSoft || theme.palette[def.tone].dark }}>
              {def.label}: {row.workerName || '—'} · {meta.sign}€{fmt(row.amount)} · {periodOf(row)}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#4B5563', mt: 0.5 }}>{def.hint}</Typography>
            <TextField
              size="small" fullWidth multiline rows={2} sx={{ mt: 1.25, bgcolor: 'background.paper' }}
              label={def.reason ? 'Arsyeja e refuzimit' : 'Shënim (opsional)'}
              required={def.reason}
              value={reason} onChange={(e) => setReason(e.target.value)}
              helperText={def.reason
                ? `Shkon te punonjësi · së paku ${MIN_REASON} shenja`
                : 'Ruhet në kronologji'}
            />
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>{error}</Alert>}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
        {mail ? (
          <>
            <Button onClick={() => { setMail(null); setError(''); }} disabled={sending}
              sx={{ textTransform: 'none', fontWeight: 700 }}>
              Anulo dërgimin
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" disableElevation onClick={sendMail}
              disabled={sending || !mail.to.trim()}
              startIcon={sending ? <CircularProgress size={15} color="inherit" /> : <EmailIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', fontWeight: 700 }}>
              {sending ? 'Duke dërguar…' : 'Dërgo vendimin'}
            </Button>
          </>
        ) : def ? (
          <>
            <Button onClick={() => { setPending(null); setReason(''); setError(''); }} disabled={busy}
              sx={{ textTransform: 'none', fontWeight: 700 }}>
              Kthehu
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" disableElevation color={def.tone} onClick={run} disabled={busy || blocked}
              startIcon={busy ? <CircularProgress size={15} color="inherit" /> : ACTION_ICON[pending]}
              sx={{ textTransform: 'none', fontWeight: 700 }}>
              {busy ? 'Duke ruajtur…' : `Konfirmo — ${def.label.toLowerCase()}`}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700 }}>Mbyll</Button>
            {!locked && onEdit && (
              <Button onClick={() => onEdit(row)} sx={{ textTransform: 'none', fontWeight: 700 }}>Ndrysho</Button>
            )}
            <Box sx={{ flex: 1 }} />
            {actions.length === 0 && !locked && (
              <Typography sx={{ fontSize: '0.72rem', color: '#9CA3AF', mr: 1 }}>
                {row.status === 'pending' ? 'Vendimin e merr HR-ja.' : 'Asnjë veprim i mundshëm.'}
              </Typography>
            )}
            {actions.map((a) => (
              <Button key={a} disableElevation
                variant={a === 'approve' || a === 'submit' ? 'contained' : 'outlined'}
                color={WORKFLOW[a].tone}
                startIcon={ACTION_ICON[a]}
                onClick={() => { setPending(a); setReason(''); setError(''); }}
                sx={{ textTransform: 'none', fontWeight: 700 }}>
                {WORKFLOW[a].label}
              </Button>
            ))}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
